import { BRAND } from '@shared/constants/brand';
import { TIMING } from '@shared/constants/limits';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const hours = (ms: number) => Math.round(ms / 3_600_000);

export function PrivacyPage() {
  useDocumentMeta({
    title: '개인정보처리방침',
    description: `${BRAND.name}의 개인정보처리방침입니다. 회원가입 없이 닉네임과 방 정보만을 짧은 시간 동안 처리하며, 그 범위와 보관 기간, 광고 쿠키 사용을 안내합니다.`,
    path: '/privacy',
  });

  return (
    <article className="prose-sm flex flex-col gap-6 leading-relaxed text-charcoal-700 animate-fade-up">
      <header>
        <h1 className="text-3xl font-extrabold text-charcoal-900">개인정보처리방침</h1>
        <p className="mt-2 text-sm text-charcoal-500">시행일: {BRAND.privacyEffectiveDate}</p>
        <p className="mt-4">
          {BRAND.name}(이하 "서비스")는 회원가입 없이 이용하는 점심 메뉴 결정 서비스입니다. 서비스는 이용에 꼭 필요한 최소한의 정보만을 짧은 시간
          동안 처리하며, 「개인정보 보호법」에 따라 이용자의 개인정보를 보호하고 관련 고충을 신속하게 처리하기 위해 다음과 같이 개인정보처리방침을
          수립·공개합니다.
        </p>
      </header>

      <Section title="1. 처리하는 개인정보의 항목과 수집 방법">
        <p>서비스는 계정을 만들지 않으며 이름, 이메일, 전화번호 등 식별 정보를 요구하지 않습니다. 이용 과정에서 다음 정보가 처리됩니다.</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>이용자가 직접 입력하는 정보</strong>: 닉네임, 방 이름, 방 비밀번호(설정한 경우). 비밀번호는 복원할 수 없는 해시 형태로만
            저장됩니다.
          </li>
          <li>
            <strong>서비스 이용 중 자동으로 생성되는 정보</strong>: 방 ID, 참여자 ID, 재접속 토큰, 선택 기록(배달/외식, 음식 종류, 메뉴), 접속 상태,
            시각 정보.
          </li>
          <li>
            <strong>자동으로 수집되는 정보</strong>: IP 주소, 브라우저 종류 등 접속 정보. 이는 과도한 요청을 제한하고 서비스를 안정적으로
            운영하기 위해 처리 중에만 일시적으로 사용되며 별도로 저장하지 않습니다.
          </li>
        </ul>
      </Section>

      <Section title="2. 개인정보의 처리 목적">
        <ul className="list-disc space-y-1 pl-5">
          <li>방 생성, 참여, 실시간 메뉴 선택 진행 등 서비스의 핵심 기능 제공</li>
          <li>새로고침이나 일시적 연결 끊김 후 같은 참여자로 복귀(재접속)</li>
          <li>부정 이용 방지 및 서비스 안정성 확보</li>
          <li>광고 게재(제5조 참고)</li>
        </ul>
      </Section>

      <Section title="3. 개인정보의 보유 및 이용 기간">
        <p>방과 관련된 모든 정보는 방이 종료되면 즉시, 종료되지 않더라도 다음 기간이 지나면 자동으로 삭제됩니다.</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>대기 중인 방: 생성 후 {hours(TIMING.EXPIRE_WAITING_MS)}시간</li>
          <li>진행 중인 방: 마지막 활동 후 {hours(TIMING.EXPIRE_PLAYING_MS)}시간</li>
          <li>완료된 방: 마지막 활동 후 {hours(TIMING.EXPIRE_COMPLETED_MS)}시간</li>
        </ul>
        <p>
          방장이 방을 종료하거나 참여자가 모두 나가면 즉시 삭제됩니다. 삭제된 정보는 복구되지 않으며, 서비스는 통계나 분석 목적으로 방 정보나
          선택 기록을 별도로 보관하지 않습니다.
        </p>
      </Section>

      <Section title="4. 브라우저 저장소(localStorage)와 쿠키">
        <p>
          서비스는 재접속을 위해 참여자 ID와 재접속 토큰을 이용자의 브라우저 저장소(localStorage)에 방별로 저장합니다. 이 정보는 이용자 기기에만
          있으며 하루가 지나면 자동으로 정리됩니다. 브라우저 설정에서 사이트 데이터를 삭제하면 즉시 제거됩니다. 서비스 자체는 로그인이나 추적
          목적의 쿠키를 사용하지 않습니다.
        </p>
      </Section>

      <Section title="5. 광고 및 제3자 서비스">
        <p>
          서비스는 운영을 위해 Google AdSense 광고를 게재할 수 있습니다. Google을 비롯한 제3자 광고 제공업체는 쿠키 및 광고 식별자를 사용하여
          이용자의 이 사이트 또는 다른 사이트 방문 기록을 기반으로 광고를 게재할 수 있습니다. 이용자는{' '}
          <a href="https://www.google.com/settings/ads" className="text-primary-600 underline" target="_blank" rel="noopener noreferrer">
            Google 광고 설정
          </a>
          에서 맞춤 광고를 해제하거나,{' '}
          <a href="https://www.aboutads.info/choices/" className="text-primary-600 underline" target="_blank" rel="noopener noreferrer">
            aboutads.info
          </a>
          에서 제3자 공급업체의 맞춤 광고 쿠키 사용을 거부할 수 있습니다. Google의 데이터 사용에 관한 자세한 내용은{' '}
          <a href="https://policies.google.com/technologies/partner-sites" className="text-primary-600 underline" target="_blank" rel="noopener noreferrer">
            Google 정책
          </a>
          을 참고하세요.
        </p>
      </Section>

      <Section title="6. 개인정보의 처리 위탁 및 국외 이전">
        <p>서비스는 안정적인 운영을 위해 다음 업체의 인프라를 이용하며, 이 과정에서 정보가 국외 서버에서 처리될 수 있습니다.</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Cloudflare, Inc.</strong>(미국): 웹 호스팅, 실시간 통신, 방 정보의 임시 저장, 보안 및 트래픽 관리. 보관 기간은 제3조와
            같습니다.
          </li>
          <li>
            <strong>Google LLC</strong>(미국): 광고 게재(제5조).
          </li>
        </ul>
      </Section>

      <Section title="7. 개인정보의 제3자 제공">
        <p>
          서비스는 이용자의 개인정보를 제3자에게 제공하지 않습니다. 다만 법령에 따라 수사기관 등이 적법한 절차로 요청하는 경우에는 예외로
          합니다. 같은 방에 참여한 이용자들에게는 서비스 제공을 위해 닉네임과 선택 내용, 접속 상태가 표시됩니다.
        </p>
      </Section>

      <Section title="8. 이용자의 권리">
        <p>
          이용자는 언제든지 방에서 나가거나(방장은 방을 종료하여) 본인의 정보를 즉시 삭제할 수 있고, 브라우저의 사이트 데이터를 삭제하여 기기에
          저장된 재접속 정보를 지울 수 있습니다. 그 밖의 열람·정정·삭제 요청은 제10조의 연락처로 문의하시면 지체 없이 처리합니다.
        </p>
      </Section>

      <Section title="9. 개인정보의 안전성 확보 조치">
        <ul className="list-disc space-y-1 pl-5">
          <li>모든 통신은 HTTPS/WSS로 암호화됩니다.</li>
          <li>방 비밀번호는 솔트가 적용된 해시로만 저장하며 재접속 토큰도 해시로 저장합니다.</li>
          <li>방 주소는 추측이 어려운 무작위 ID로 생성되며, 방 페이지는 검색엔진에 노출되지 않도록 설정되어 있습니다.</li>
          <li>방 정보는 정해진 기간이 지나면 자동으로 삭제됩니다.</li>
        </ul>
      </Section>

      <Section title="10. 개인정보 보호책임자 및 문의">
        <p>
          개인정보 처리에 관한 문의, 불만 처리, 피해 구제 요청은 아래로 연락해 주세요.
          <br />
          이메일:{' '}
          <a href={`mailto:${BRAND.contactEmail}`} className="text-primary-600 underline">
            {BRAND.contactEmail}
          </a>
        </p>
        <p>
          기타 개인정보 침해 신고나 상담은 개인정보침해신고센터(privacy.kisa.or.kr, 국번 없이 118), 개인정보분쟁조정위원회(kopico.go.kr,
          1833-6972)에 문의할 수 있습니다.
        </p>
      </Section>

      <Section title="11. 개인정보처리방침의 변경">
        <p>이 방침은 법령이나 서비스 변경에 따라 수정될 수 있으며, 변경 시 이 페이지에 시행일과 함께 공지합니다.</p>
      </Section>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-lg font-bold text-charcoal-900">{title}</h2>
      {children}
    </section>
  );
}
